/**
 * 表示対象（配送形態・区間種別）の絞り込み。
 *
 * 「チェックを外した値を持っている案件だけを隠す」除外方式。設定されていない案件（null・空文字）は
 * どのフラグも立っていない扱いで常に残す。dev では配送形態が 97% 未設定で、包含方式（選択中の値を
 * 持つ案件だけ表示）にすると 1 つ外した時点でほぼ全件消えるため、この方式を採る。
 *
 * 状態は「隠している値の集合」で持つ。全選択が既定なので、集合が空 = 絞り込み無しになり、
 * 選択肢が増えても既定値の作り直しが要らない。
 */
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';

/** 配送形態。ShipmentOrder::DELIVERY_METHODS と同じ並び */
export const DELIVERY_METHODS = ['貸切', '共配', '路線便'] as const;
/** 区間種別。ShipmentOrderLeg::STATS_OPTIONS の値を画面の並び（集荷→配送→横持ち）で持つ */
export const SEGMENT_STATS = ['集荷', '配送', '横持ち'] as const;

export type OrderAttrFilter = {
  /** 非表示にしている配送形態 */
  hiddenDeliveryMethods: Set<string>;
  /** 非表示にしている区間種別 */
  hiddenSegmentStats: Set<string>;
};

export const EMPTY_ORDER_ATTR_FILTER: OrderAttrFilter = {
  hiddenDeliveryMethods: new Set(),
  hiddenSegmentStats: new Set(),
};

/** 判定に必要な属性だけを要求する。未割当リストと車両カードで型が違うため */
type OrderAttrs = Pick<ShipmentOrderLegRunForPlanEntity, 'delivery_method' | 'leg_stats'>;

export const isOrderAttrFilterActive = (filter: OrderAttrFilter): boolean => (
  filter.hiddenDeliveryMethods.size > 0 || filter.hiddenSegmentStats.size > 0
);

const isHidden = (value: string | null | undefined, hidden: Set<string>): boolean => (
  !!value && hidden.has(value)
);

/** 表示してよい案件か。隠した配送形態・区間種別のどちらかに当たれば false */
export const matchesOrderAttrs = (order: OrderAttrs, filter: OrderAttrFilter): boolean => (
  !isHidden(order.delivery_method, filter.hiddenDeliveryMethods)
  && !isHidden(order.leg_stats, filter.hiddenSegmentStats)
);

/**
 * 未割当リストの絞り込み。判定は表示 VM ではなく元データ（source）で行う。
 * source を持たない item（地点単位の合成カードなど）は隠す根拠が無いので残す。
 */
export const filterUnassignedItemsByOrderAttrs = <T extends { source?: OrderAttrs }>(
  items: T[],
  filter: OrderAttrFilter,
): T[] => {
  if (!isOrderAttrFilterActive(filter)) return items;
  return items.filter((item) => !item.source || matchesOrderAttrs(item.source, filter));
};

/**
 * 車両を隠すかの判定。隠した値の案件を 1 件でも積んでいたらその車両ごと隠す。
 * 案件を積んでいない車両は判定対象にしない（絞り込みで空車が消えないようにする）。
 */
export const hasHiddenOrderAttrs = (orders: OrderAttrs[], filter: OrderAttrFilter): boolean => {
  if (!isOrderAttrFilterActive(filter)) return false;
  return orders.some((order) => !matchesOrderAttrs(order, filter));
};

/** チェックの ON/OFF。新しい Set を返し、呼び出し側の state を書き換えない */
export const toggledHiddenValue = (hidden: Set<string>, value: string): Set<string> => {
  const next = new Set(hidden);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
};
